import React, {
    useState,
    useEffect,
    useRef,
    useMemo,
    useCallback,
} from "react";
import {
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    FormGroup,
} from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";
import Select, { components } from "react-select";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { TextField } from "@material-ui/core";
import Radio from "@mui/material/Radio";
import FormControlLabel from "@mui/material/FormControlLabel";
import RadioGroup from "@mui/material/RadioGroup";

/* ── Helpers y constantes fuera del componente ──────────────────────────
   Definirlos aquí evita recrearlos en cada render. */

const pad = (n) => String(n).padStart(2, "0");

const formatTime = (date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
    return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
        date.getSeconds()
    )}`;
};

// Opciones fijas del nuevo combo de servicios (¿cantidad de servicios?)
const SERVICIOS_TRASLADO_OPTIONS = [
    { value: "transporte_usuario", label: "Transporte usuario" },
    { value: "transporte_acompanante", label: "Transporte acompañante" },
    { value: "transporte_alimentacion", label: "Transporte alimentación" },
];

// Opción personalizada que muestra un checkbox (combo box con check)
const CheckboxOption = (props) => (
    <components.Option {...props}>
        <input
            type="checkbox"
            checked={props.isSelected}
            onChange={() => null}
            style={{ marginRight: 8 }}
        />
        <span>{props.label}</span>
    </components.Option>
);

const ModalCreate = ({
    isOpen,
    toggle,
    title,
    token,
    updatedTable,
    content,
    setModalOpen,
    modalOpen,
}) => {
    // Listas que vienen del backend
    const [numeroCaso, setNumeroCaso] = useState([]);
    const [hogarTraslado, setHogarTraslado] = useState([]);
    const [tipoServicioHogar, setTipoServicioHogar] = useState([]);
    const [servicios, setServicios] = useState([]);

    // Datos derivados del caso seleccionado
    const [hogarInicial, setHogarInicial] = useState(null);
    const [nombrePaciente, setNombrePaciente] = useState("");
    const [acompanantesMax, setAcompanantesMax] = useState("");

    // Selecciones (guardamos solo el objeto de react-select; el id se deriva)
    const [selectedNumeroCaso, setSelectedNumeroCaso] = useState(null);
    const [selectedHogarTraslado, setSelectedHogarTraslado] = useState(null);
    const [selectedServicioHogar, setSelectedServicioHogar] = useState(null);
    const [selectedServicio, setSelectedServicio] = useState(null);
    const [serviciosTraslado, setServiciosTraslado] = useState([]); // multi-check

    // Campos del formulario
    const [fechaServicio, setFechaServicio] = useState("");
    const [rutaTraslado, setRutraTraslado] = useState("");
    const [horaCita, setHoraCita] = useState(null); // Date | null (lo pide MUI)
    const [horaSalida, setHoraSalida] = useState(null); // Date | null
    const [observaciones, setObservaciones] = useState("");
    const [numeroAcompanantes, setNumeroAcompanantes] = useState("");
    const [mostrarServicios, setMostrarServicios] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const modalRef = useRef(null);

    // Id del caso seleccionado, derivado del objeto seleccionado
    const casoId = selectedNumeroCaso?.value ?? null;

    /* ── Instancia de axios con el token ya configurado ───────────────────
       Evita repetir el header Authorization en cada petición. */
    const api = useMemo(
        () =>
            axios.create({
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            }),
        [token]
    );

    /* ── Carga inicial: las 3 listas en paralelo (1 solo efecto) ──────────── */
    useEffect(() => {
        if (!token) return;
        let active = true;

        Promise.all([
            api.get("/programacionformulario/case-number"),
            api.get("/hogar"),
            api.get("/tiposerviciohogar"),
        ])
            .then(([casos, hogares, tipos]) => {
                if (!active) return;
                setNumeroCaso(casos.data);
                setHogarTraslado(hogares.data);
                setTipoServicioHogar(tipos.data);
            })
            .catch((error) => console.log(error));

        return () => {
            active = false;
        };
    }, [api, token]);

    /* ── Al elegir un caso: servicios + hogar en paralelo ─────────────────── */
    useEffect(() => {
        if (!token || !casoId) return;
        let active = true;

        Promise.all([
            api.get(`/formulario/consulta/servicios/${casoId}`),
            api.get(`/formulario/consulta/hogar/${casoId}`),
        ])
            .then(([serv, hogar]) => {
                if (!active) return;
                setServicios(serv.data ?? []);
                setHogarInicial(hogar.data);
                setNombrePaciente(hogar.data?.paciente?.Nombre ?? "");
                setAcompanantesMax(hogar.data?.cantidad_acompanantes ?? "");
            })
            .catch((error) => console.log(error));

        return () => {
            active = false;
        };
    }, [api, token, casoId]);

    /* ── Autollenado de observaciones para discapacidad con rampa ─────────── */
    useEffect(() => {
        const necesitaRampa = hogarInicial?.condiciones_especiales?.some(
            (element) => element.Texto === "Discap. con Rampa"
        );
        if (necesitaRampa && observaciones === "") {
            setObservaciones("Se necesita carro con Rampa");
        }
    }, [hogarInicial, observaciones]);

    /* ── Opciones de los selects (memoizadas) ─────────────────────────────── */
    const numeroCasoOptions = useMemo(
        () =>
            numeroCaso.map((option) => ({
                value: option.Id,
                label: option.NumeroCaso,
            })),
        [numeroCaso]
    );

    const hogarTrasladoOptions = useMemo(
        () =>
            hogarTraslado.map((option) => ({
                value: option.id,
                label: option.Nombre,
            })),
        [hogarTraslado]
    );

    const tipoServicioHogarOptions = useMemo(
        () =>
            tipoServicioHogar.map((option) => ({
                value: option.id,
                label: option.Texto,
            })),
        [tipoServicioHogar]
    );

    const servicioDiscriminado = useMemo(
        () =>
            servicios.flatMap((option) => {
                if (option.servicio_maestro?.FormularioId) {
                    return option.servicio_maestro.servicio_detalle.map(
                        (detalle) => ({
                            value: detalle.id,
                            label: detalle.tipo_servicio.Texto,
                        })
                    );
                }
                return [{ value: "N.A", label: "N.A" }];
            }),
        [servicios]
    );

    // El hogar va deshabilitado: su valor se deriva del caso si no hay selección
    const hogarValue = useMemo(
        () =>
            selectedHogarTraslado ??
            hogarTrasladoOptions.find(
                (option) => option.value === hogarInicial?.HogarId
            ) ??
            null,
        [selectedHogarTraslado, hogarTrasladoOptions, hogarInicial]
    );

    /* ── Handlers (memoizados) ────────────────────────────────────────────── */
    const handleNumeroAcompanantes = useCallback(
        (event) => {
            const value = event.target.value;
            if (value === "" || Number(value) <= Number(acompanantesMax)) {
                setNumeroAcompanantes(value);
            } else {
                Swal.fire({
                    icon: "error",
                    title: "Error!",
                    text:
                        "El numero de acompañantes debe ser menor o igual al numero de acompañantes registrados " +
                        acompanantesMax,
                    showCloseButton: true,
                });
            }
        },
        [acompanantesMax]
    );

    const handleModalToggle2 = useCallback(() => {
        setModalOpen(!modalOpen);
        setSelectedNumeroCaso(null);
        setSelectedHogarTraslado(null);
        setSelectedServicioHogar(null);
        setSelectedServicio(null);
        setServiciosTraslado([]);
        setNombrePaciente("");
        setHogarInicial(null);
        setFechaServicio("");
        setHoraCita(null);
        setHoraSalida(null);
        setObservaciones("");
        setRutraTraslado("");
        setNumeroAcompanantes("");
        setMostrarServicios(false);
    }, [modalOpen, setModalOpen]);

    const handleCreateSubmit = useCallback(
        (event) => {
            event.preventDefault();
            setIsSubmitting(true);

            api.post("/programacionformularios", {
                FechaServicio: fechaServicio,
                HogarId: hogarValue?.value ?? "",
                FormularioId: casoId,
                TipoServicioHogarId: selectedServicioHogar?.value ?? "",
                ServicioDetalleId: selectedServicio?.value ?? " ",
                RutaTraslado: rutaTraslado,
                HoraCita: formatTime(horaCita),
                HoraSalida: formatTime(horaSalida),
                Observacion: observaciones,
                CantidadAcompanantes: numeroAcompanantes,
                // Nuevo: lista de servicios marcados en el combo con check
                ServiciosTraslado: serviciosTraslado.map((s) => s.value),
            })
                .then(() => {
                    Swal.fire({
                        icon: "success",
                        title: "Creado!",
                        text: "La información de traslado a sido creada con exito",
                        showCloseButton: true,
                    }).then(() => {
                        updatedTable();
                        handleModalToggle2();
                        setIsSubmitting(false);
                    });
                })
                .catch(() => {
                    setIsSubmitting(false);
                    Swal.fire({
                        icon: "error",
                        title: "Error!",
                        text: "Ha ocurrido un error al guardar la información de traslado.",
                        showCloseButton: true,
                    });
                });
        },
        [
            api,
            fechaServicio,
            hogarValue,
            casoId,
            selectedServicioHogar,
            selectedServicio,
            rutaTraslado,
            horaCita,
            horaSalida,
            observaciones,
            numeroAcompanantes,
            serviciosTraslado,
            updatedTable,
            handleModalToggle2,
        ]
    );

    return (
        <Modal ref={modalRef} isOpen={isOpen} toggle={handleModalToggle2}>
            <ModalHeader toggle={handleModalToggle2}>{title}</ModalHeader>
            <ModalBody>
                <FormGroup>
                    <label htmlFor="NumeroCaso">Numero de Caso:</label>
                    <Select
                        options={numeroCasoOptions}
                        value={selectedNumeroCaso}
                        onChange={setSelectedNumeroCaso}
                    />
                </FormGroup>

                <FormGroup>
                    <label>Paciente:</label>
                    <input
                        className="form-control"
                        name="paciente"
                        type="text"
                        value={nombrePaciente}
                        readOnly
                    />
                </FormGroup>

                <FormGroup>
                    <label htmlFor="Hogar">Hogar:</label>
                    <Select
                        isDisabled
                        options={hogarTrasladoOptions}
                        value={hogarValue}
                        onChange={setSelectedHogarTraslado}
                    />
                </FormGroup>

                <FormGroup>
                    <label htmlFor="ServicioHogar">Servicio del hogar:</label>
                    <Select
                        options={tipoServicioHogarOptions}
                        value={selectedServicioHogar}
                        onChange={setSelectedServicioHogar}
                    />
                </FormGroup>

                <FormGroup>
                    <label>
                        ¿Desea añadir información sobre el servicio a la ruta de
                        traslado? :
                    </label>
                    <RadioGroup
                        aria-labelledby="demo-radio-buttons-group-label"
                        name="radio-buttons-group"
                        onChange={(e) =>
                            setMostrarServicios(e.target.value === "Si")
                        }
                    >
                        <FormControlLabel
                            value="Si"
                            control={<Radio />}
                            label="SI"
                        />
                        <FormControlLabel
                            value="No"
                            control={<Radio />}
                            label="No"
                        />
                    </RadioGroup>
                </FormGroup>

                {mostrarServicios && (
                    <FormGroup>
                        <label htmlFor="CitaMedica">Cita Medica:</label>
                        <Select
                            options={servicioDiscriminado}
                            value={selectedServicio}
                            onChange={setSelectedServicio}
                        />
                    </FormGroup>
                )}

                <FormGroup>
                    <label>Fecha de Servicio</label>
                    <TextField
                        className="form-control"
                        type="date"
                        name="FechaSolicitud"
                        value={fechaServicio}
                        onChange={(e) => setFechaServicio(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                </FormGroup>

                <FormGroup>
                    <label>Ruta de traslado:</label>
                    <input
                        className="form-control"
                        name="rutaTraslado"
                        type="text"
                        value={rutaTraslado}
                        onChange={(e) => setRutraTraslado(e.target.value)}
                    />
                </FormGroup>

                <FormGroup>
                    <label>Numero de Acompañantes en el traslado:</label>
                    <input
                        className="form-control"
                        name="numeroAcompanantes"
                        type="number"
                        value={numeroAcompanantes}
                        onChange={handleNumeroAcompanantes}
                    />
                </FormGroup>

                {/* ── Nuevo: combo box con check (¿cantidad de servicios?) ── */}
                <FormGroup>
                    <label htmlFor="ServiciosTraslado">
                        ¿Cantidad de servicios?
                    </label>
                    <Select
                        inputId="ServiciosTraslado"
                        options={SERVICIOS_TRASLADO_OPTIONS}
                        value={serviciosTraslado}
                        onChange={(value) => setServiciosTraslado(value ?? [])}
                        isMulti
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        components={{ Option: CheckboxOption }}
                        placeholder="Seleccione los servicios..."
                    />
                </FormGroup>

                <FormGroup>
                    <label>Hora de la cita:</label>
                    <br />
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <TimePicker
                            value={horaCita}
                            onChange={setHoraCita}
                            renderInput={(params) => <TextField {...params} />}
                        />
                    </LocalizationProvider>
                </FormGroup>

                <FormGroup>
                    <label>Hora de salida:</label>
                    <br />
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <TimePicker
                            value={horaSalida}
                            onChange={setHoraSalida}
                            renderInput={(params) => <TextField {...params} />}
                        />
                    </LocalizationProvider>
                </FormGroup>

                <FormGroup>
                    <label>observaciones:</label>
                    <input
                        className="form-control"
                        name="observaciones"
                        type="text"
                        value={observaciones}
                        onChange={(e) => setObservaciones(e.target.value)}
                    />
                </FormGroup>
            </ModalBody>

            <ModalFooter>
                <Button
                    onClick={handleCreateSubmit}
                    color="primary"
                    type="submit"
                    disabled={isSubmitting}
                >
                    {isSubmitting ? "Enviando..." : "Crear"}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default ModalCreate;